export default function Product360(product360, product360AssetDirectory, deviceType) {
   var genericGet = function(variable) {
      return function() {
         return this[variable];
      };
   };

   var genericSet = function(variable) {
      return function(value) {
         this[variable] = value;
      };
   };

   // Classes

   // Product360Pointer
   // An abstraction for mouse and touch event handling.
   var Product360Pointer = function(pointerElement) {
      // Variables
      this.pointerInitial = [0, 0];
      this.pointerCurrent = [0, 0];
      this.pointerState = 'up'; // 'up', 'down'
      this.pointerdownTime = 0;
      this.onpointerdown = function() {};
      this.onpointermove = function() {};
      this.onpointerup = function() {};
      this.onclick = function() {};

      // Getters and setters
      var getPointerInitial = genericGet('pointerInitial').bind(this);
      var setPointerInitial = genericSet('pointerInitial').bind(this);

      var getPointerCurrent = genericGet('pointerCurrent').bind(this);
      var setPointerCurrent = genericSet('pointerCurrent').bind(this);

      var getPointerState = genericGet('pointerState').bind(this);
      var setPointerState = genericSet('pointerState').bind(this);

      var getPointerdownTime = genericGet('pointerdownTime').bind(this);
      var setPointerdownTime = genericSet('pointerdownTime').bind(this);

      var getOnpointerdown = genericGet('onpointerdown').bind(this);
      var setOnpointerdown = genericSet('onpointerdown').bind(this);

      var getOnpointermove = genericGet('onpointermove').bind(this);
      var setOnpointermove = genericSet('onpointermove').bind(this);

      var getOnpointerup = genericGet('onpointerup').bind(this);
      var setOnpointerup = genericSet('onpointerup').bind(this);

      var getOnclick = genericGet('onclick').bind(this);
      var setOnclick = genericSet('onclick').bind(this);

      var getPointerDelta = function() {
         return [
            getPointerCurrent()[0] - getPointerInitial()[0],
            getPointerCurrent()[1] - getPointerInitial()[1],
         ];
      };

      // Functions
      var getPointerFromMouseOrTouchEvent = function(ev) {
         var pointerEvent = ev.event instanceof MouseEvent ? ev.event : ev.event.touches[0];

         return [pointerEvent.clientX, pointerEvent.clientY];
      };

      var distanceLessThan = function(initialPos, finalPos, distance) {
         var x = initialPos[0] - finalPos[0];
         var y = initialPos[1] - finalPos[1];
         return x * x + y * y < distance * distance;
      };

      // Listeners
      var onpointerdownFunction = function(ev) {
         var pointer = getPointerFromMouseOrTouchEvent(ev);

         ev.preventDefault();
         setPointerState('down');
         setPointerdownTime(product360Animation.getAnimationPlaybackTime());
         setPointerInitial(pointer);
         setPointerCurrent(pointer);

         getOnpointerdown()(ev);
      };

      var onpointermoveFunction = function(ev) {
         if (getPointerState() !== 'up') {
            ev.preventDefault();
            ev.stopPropagation();

            setPointerCurrent(getPointerFromMouseOrTouchEvent(ev));
            getOnpointermove()(ev);
         }
      };

      var onpointerupFunction = function(ev) {
         if (getPointerState() !== 'up') {
            ev.preventDefault();
            setPointerState('up');
            getOnpointerup()(ev);

            // Consider it a "click" event if the user dragged less than 15px.
            var initial = getPointerInitial();
            var current = getPointerCurrent();
            if (distanceLessThan(initial, current, 15)) {
               getOnclick()(ev);
            }
         }
      };

      pointerElement.addEvent('mousedown', onpointerdownFunction);
      window.addEvent('mousemove', onpointermoveFunction);
      window.addEvent('mouseup', onpointerupFunction);

      // Public object
      return {
         getPointerdownTime: getPointerdownTime,
         getPointerDelta: getPointerDelta,
         getPointerState: getPointerState,
         setOnpointerdown: setOnpointerdown,
         setOnpointermove: setOnpointermove,
         setOnpointerup: setOnpointerup,
         setOnclick: setOnclick,
      };
   };

   // Product360Animation
   // Controller for the animation rotation state.
   var Product360Animation = function(spritesheet) {
      // Variables
      this.animationFrames = 240;
      this.animationZoomFrameOffset = 3; // Only zoom to every third frame
      this.animationDuration = 8;
      this.animationSpriteColumns = 16;
      this.animationSpriteRows = 15;
      this.animationPlaybackTime = 0; // seconds
      this.animationTime = 0; // milliseconds

      // Getters and setters
      var getAnimationFrames = genericGet('animationFrames').bind(this);

      var getAnimationZoomFrameOffset = genericGet('animationZoomFrameOffset').bind(this);

      var getAnimationDuration = genericGet('animationDuration').bind(this);

      var getAnimationSpriteColumns = genericGet('animationSpriteColumns').bind(this);

      var getAnimationSpriteRows = genericGet('animationSpriteRows').bind(this);

      var getAnimationPlaybackTime = genericGet('animationPlaybackTime').bind(this);
      var setAnimationPlaybackTime = genericSet('animationPlaybackTime').bind(this);

      var getAnimationTime = genericGet('animationTime').bind(this);
      var setAnimationTime = genericSet('animationTime').bind(this);

      var getAnimationFrame = function() {
         return Math.floor(
            (getAnimationPlaybackTime() / getAnimationDuration()) * getAnimationFrames()
         );
      };

      var getAnimationZoomFrame = function() {
         var frame = getAnimationFrame();
         var offset = getAnimationZoomFrameOffset();
         var frames = getAnimationFrames();
         var left = Math.floor(frame / offset) * offset;
         var right = Math.min(left + offset, frames);

         return (right - frame > frame - left ? left : right % frames) + 1;
      };

      var getCurrentSpritePositionX = function() {
         return (
            ((getAnimationFrame() % getAnimationSpriteColumns()) * 100) /
            getAnimationSpriteColumns()
         );
      };

      var getCurrentSpritePositionY = function() {
         return (
            (Math.floor(getAnimationFrame() / getAnimationSpriteColumns()) * 100) /
            getAnimationSpriteRows()
         );
      };

      var getAnimationCssString = function() {
         return (
            'translateX(-' +
            getCurrentSpritePositionX() +
            '%) translateY(-' +
            getCurrentSpritePositionY() +
            '%)'
         );
      };

      var getAnimationPlaybackTimeLooped = function(seconds) {
         var duration = getAnimationDuration();
         return ((seconds % duration) + duration) % duration;
      };

      var getAnimationPlaybackTimeAdded = function(seconds) {
         var playbackTime = getAnimationPlaybackTime() + seconds;
         return getAnimationPlaybackTimeLooped(playbackTime);
      };

      // Size the spritesheet relative to the container element
      requestAnimationFrame(function() {
         spritesheet.style.width = getAnimationSpriteColumns() * 100 + '%';
         spritesheet.style.height = getAnimationSpriteRows() * 100 + '%';
      });

      // Public object
      return {
         getAnimationDuration: getAnimationDuration,
         getAnimationTime: getAnimationTime,
         setAnimationTime: setAnimationTime,
         getAnimationPlaybackTime: getAnimationPlaybackTime,
         setAnimationPlaybackTime: setAnimationPlaybackTime,
         getAnimationFrame: getAnimationFrame,
         getAnimationZoomFrame: getAnimationZoomFrame,
         getAnimationCssString: getAnimationCssString,
         getAnimationPlaybackTimeLooped: getAnimationPlaybackTimeLooped,
         getAnimationPlaybackTimeAdded: getAnimationPlaybackTimeAdded,
      };
   };

   // Product360Rect
   // A class to store the offset width and height of an element without
   // constantly re-inspecting the dom, which causes performances problems.
   var Product360Rect = function(element) {
      this.rect = {};

      var getRect = genericGet('rect').bind(this);
      var setRect = genericSet('rect').bind(this);

      var updateRect = function() {
         var rect = {
            width: element.offsetWidth,
            height: element.offsetHeight,
         };

         setRect(rect);
      };

      // updateRect on creation
      updateRect();

      // Public object
      return {
         getRect: getRect,
         updateRect: updateRect,
      };
   };

   // Variables
   var product360TransformerPlaceholder = product360.getElement(
      '.product-360-transformer-placeholder'
   );
   var product360TransformerInactive = product360.getElement('.product-360-transformer-inactive');
   var product360TransformerActive = product360.getElement('.product-360-transformer-active');
   var product360DesktopZoom = product360.getElement('.product-360-desktop-zoom');
   var product360Rect = new Product360Rect(product360);
   var product360TransformerActiveRect = new Product360Rect(product360TransformerActive);
   var product360Pointer = new Product360Pointer(product360);
   var product360Animation = new Product360Animation(product360TransformerInactive);
   var product360State = 'off'; // 'on' 'off'
   var product360SpritesheetIsLoaded = false;

   // Getters and setters
   var getProduct360State = function() {
      return product360State;
   };

   var setProduct360State = function(state) {
      product360State = state;
      product360.set('data-product-360-on', state === 'on' ? '' : null);
   };

   var getProduct360SpriteSheetIsLoaded = function() {
      return product360SpritesheetIsLoaded;
   };

   var setProduct360SpritesheetIsLoaded = function(isLoaded) {
      product360SpritesheetIsLoaded = isLoaded;
   };

   // Functions
   var product360CallIfOn = function(fn) {
      return function() {
         if (getProduct360State() === 'on' && getProduct360SpriteSheetIsLoaded()) {
            fn.apply(null, arguments);
         }
      };
   };

   var product360ToggleFactory = function(turnOnForce) {
      return function(bool, onLoad) {
         // Get boolean on / off value if boolean variable isn't set
         var turnOn = bool === undefined ? getProduct360State() === 'off' : bool;

         if (turnOnForce !== undefined) {
            turnOn = turnOnForce;
         }

         var stateToApply = turnOn ? 'on' : 'off';

         setProduct360State(stateToApply);

         // Start animation if turning on
         if (turnOn) {
            var product360TransformerInactiveImg = Asset.image(
               product360AssetDirectory + '/512/grid.jpg',
               {
                  onload: function(el) {
                     product360SpriteSheetOnLoad(el);
                     if (typeof onLoad === 'function') {
                        onLoad();
                     }
                  },
               }
            );

            product360TransformerInactive.empty();
            product360TransformerInactive.grab(product360TransformerInactiveImg);
         }
      };
   };

   var product360SpriteSheetOnLoad = function(el) {
      requestAnimationFrame(function(animationTime) {
         setProduct360SpritesheetIsLoaded(true);
         product360RequestAnimation(animationTime);
      });
   };

   var product360RequestAnimation = product360CallIfOn(function(animationTime) {
      var animationFunctions = {
         up: product360AnimateRotate,
         down: product360AnimateScrub,
      };

      var animationFunction =
         animationFunctions[product360Pointer.getPointerState()] || function() {};

      product360Animation.setAnimationTime(animationTime);
      requestAnimationFrame(function(newAnimationTime) {
         animationFunction(newAnimationTime);
         product360RequestAnimation(newAnimationTime);
      });
   });

   var product360AnimateRotate = function(animationTime) {
      if (document.body.hasClass('zoom-overlay-active')) {
         return;
      }

      var timeSinceLastAnimation = (animationTime - product360Animation.getAnimationTime()) / 1000;

      product360Animation.setAnimationPlaybackTime(
         product360Animation.getAnimationPlaybackTimeAdded(timeSinceLastAnimation)
      );

      product360TransformerInactive.setStyles({
         'ms-transform': product360Animation.getAnimationCssString(),
         transform: product360Animation.getAnimationCssString(),
      });
   };

   var product360AnimateScrub = function(animationTime) {
      var playBackTime =
         -(
            (product360Pointer.getPointerDelta()[0] / product360Rect.getRect().width) *
            product360Animation.getAnimationDuration()
         ) + product360Pointer.getPointerdownTime();

      product360Animation.setAnimationPlaybackTime(
         product360Animation.getAnimationPlaybackTimeLooped(playBackTime)
      );

      product360TransformerInactive.setStyles({
         'ms-transform': product360Animation.getAnimationCssString(),
         transform: product360Animation.getAnimationCssString(),
      });
   };

   var product360ZoomCreateImageElement = function(sizes) {
      var frame = product360Animation.getAnimationZoomFrame().toString();

      return new Element('img', {
         srcset:
            product360AssetDirectory +
            '/512/' +
            frame +
            '.jpg' +
            ' 512w, ' +
            product360AssetDirectory +
            '/768/' +
            frame +
            '.jpg' +
            ' 768w, ' +
            product360AssetDirectory +
            '/1024/' +
            frame +
            '.jpg' +
            ' 1024w, ' +
            product360AssetDirectory +
            '/2048/' +
            frame +
            '.jpg' +
            ' 2048w, ' +
            product360AssetDirectory +
            '/4096/' +
            frame +
            '.jpg' +
            ' 4096w',
         sizes: sizes,
         src: product360AssetDirectory + '/1024/' + frame + '.jpg',
         width: product360Rect.getRect().width,
         height: product360Rect.getRect().height,
         'data-full-width': 4096,
         'data-full-height': 2160,
      });
   };

   var product360ZoomImageDesktop = function() {
      if (document.body.hasClass('zoom-overlay-active')) {
         return;
      }

      var zoomImageDesktop = product360ZoomCreateImageElement('100vw');
      zoomImageDesktop.setAttribute('data-action', 'zoom');

      product360DesktopZoom.empty();
      product360DesktopZoom.grab(zoomImageDesktop);

      setTimeout(function() {
         // Click the new image so that it zooms in. It's hacky, but it's
         // simpler than forking `zoom.js` to expose a `zoom` method.
         zoomImageDesktop.click();
      }, 0);
   };

   // Listeners
   product360Pointer.setOnpointerdown(
      product360CallIfOn(function(ev) {
         product360Rect.updateRect();
         product360TransformerActiveRect.updateRect();
      })
   );

   product360Pointer.setOnclick(
      product360CallIfOn(function(ev) {
         if (deviceType === 'desktop') {
            product360ZoomImageDesktop();
         }
      })
   );

   // Add the placeholder image on initialization
   product360TransformerPlaceholder.grab(
      new Element('img', {
         src: product360AssetDirectory + '/512/1.jpg',
      })
   );

   // Public object
   return {
      on: function(onLoad) {
         var toggle = product360ToggleFactory();
         return toggle(true, onLoad);
      },
      off: product360ToggleFactory(false),
      toggle: product360ToggleFactory(),
      getState: getProduct360State,
   };
}
